import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import useDialogA11y from './dialogA11y';

// ============================================================================
// A11yDialog — elle yazılmış perde/kart ikililerinin ORTAK kabuğu (FE-25)
// ============================================================================
// GÖRSEL KİMLİK DEĞİŞMEZ: perde ve kart, çağıran dosyanın KENDİ styled
// bileşenleri olarak `overlay`/`card` prop'larıyla geçilir. Bu bileşen yalnız
// DAVRANIŞI (ESC, odak tuzağı, `role="dialog"`, perde arkasını gizleme,
// veri-kaybı koruması) tek kopyaya indirir. Böylece ATS diyaloglarının görsel
// dili (genişlik, iç boşluk, gölge, dar ekran kuralları) bire bir korunur —
// Parti 10'un `max-height`/`@media` düzeltmeleri dahil.
//
// Ev kuralı "form = GenericFormDrawer, modal ASLA" DEĞİŞMEDİ: bu kabuk YENİ form
// açmak için değil, MEVCUT diyalogları veri kaybetmez ve klavyeyle kullanılır
// kılmak içindir. Çekmeceye taşıma ayrı bir iş kalemidir.
const DiscardLayer = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.97);
  border-radius: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  text-align: center;
  z-index: 1;
`;

const DiscardTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DiscardMessage = styled.p`
  margin: 0;
  max-width: 340px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  line-height: 1.5;
`;

// FE-40 — istemci doğrulaması TOAST ile bildiriliyordu; ev kuralı toast'ı yalnız
// Axios interceptor'a bırakır. Toast ayrıca diyaloğun DIŞINDA, sağ üstte belirip
// kayboluyordu: odağı formda olan klavye kullanıcısı ona hiç ulaşamıyordu. Mesaj
// artık diyaloğun İÇİNDE, aksiyonların hemen üstünde ve `role="alert"`.
export const DialogErrorLine = styled.p`
  margin: 0 0 10px;
  padding: 8px 10px;
  border-left: 3px solid ${({ theme }) => theme.colors.auxiliar.danger};
  border-radius: 4px;
  background: rgba(229, 57, 53, 0.06);
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  font-size: 12.5px;
  line-height: 1.5;
`;

const DiscardActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

// Atma onayı kartın İÇİNE, üstüne binen bir katman olarak çizilir. Ayrı bir
// `position: fixed` perde açılmaz: ikinci bir sabit katman z-index yarışına
// girer (ConfirmDialog'un 2100'ü tam da bu yüzden var) ve odak tuzağı iki kap
// arasında bölünür.
export default function A11yDialog({
  open,
  onClose,
  dirty = false,
  closeOnOutsideClick = false,
  label,
  overlay: Overlay,
  card: Card,
  overlayProps = {},
  cardProps = {},
  children,
}) {
  const { t } = useTranslation();
  const {
    dialogRef, titleId, dialogProps, overlayHandlers,
    discardPrompt, cancelDiscard, confirmDiscard,
  } = useDialogA11y({ open, onClose, dirty, closeOnOutsideClick });
  const keepEditingRef = useRef(null);

  // Atma onayı açılınca: (a) kart kaydırılmışsa başa sar — katman `inset: 0` ile
  // kartın padding kutusuna çapalı, kaydırılmış bir kartta görüş alanının dışında
  // kalırdı; (b) odağı "Düzenlemeye Dön"e taşı — güvenli olan seçenek.
  useEffect(() => {
    if (!discardPrompt) return;
    if (dialogRef.current) dialogRef.current.scrollTop = 0;
    keepEditingRef.current?.focus();
  }, [discardPrompt, dialogRef]);

  if (!open) return null;

  // `label` verilmişse erişilebilir ad odur; verilmemişse çağıran, render
  // prop'tan gelen `titleId`'yi kendi başlık elemanına koymak ZORUNDADIR
  // (`aria-labelledby` boş bir id'yi işaret ederse başlık hiç okunmaz).
  const labelProps = label
    ? { 'aria-label': label, 'aria-labelledby': undefined }
    : {};

  return (
    <Overlay {...overlayProps} {...overlayHandlers}>
      <Card
        ref={dialogRef}
        {...dialogProps}
        {...labelProps}
        {...cardProps}
        style={{ position: 'relative', ...(cardProps.style || {}) }}
      >
        {typeof children === 'function' ? children({ titleId }) : children}
        {discardPrompt && (
          <DiscardLayer
            data-dialog-trap-root
            data-testid="dialog-discard-prompt"
            role="alertdialog"
            aria-label={t('dialog.unsavedChanges.title')}
          >
            <DiscardTitle>{t('dialog.unsavedChanges.title')}</DiscardTitle>
            <DiscardMessage>{t('dialog.unsavedChanges.message')}</DiscardMessage>
            <DiscardActions>
              <Button ref={keepEditingRef} variant="outline" onClick={cancelDiscard}>
                {t('dialog.unsavedChanges.keepEditing')}
              </Button>
              <Button variant="danger" onClick={confirmDiscard}>
                {t('dialog.unsavedChanges.discard')}
              </Button>
            </DiscardActions>
          </DiscardLayer>
        )}
      </Card>
    </Overlay>
  );
}
